import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import { Switch, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export type ActionType = "viewing" | "creating" | "editing" | "deleting";

export interface PermissionState {
    model: string;
    actions: ActionType[];
    branches: "all" | string[];
}

interface InitialClientData {
    _id?: string;
    name: string;
    phone: string;
    permissions: PermissionState[];
    isOwner?: boolean;
    isScanner?: boolean;
}

export interface ClientsFormDetails {
    clientId?: string;
    initialData?: InitialClientData;
    isDuplicate?: boolean;
    onSuccess: () => void;
}

interface FormState {
    name: string;
    phone: string;
    makeOwner: boolean;
    makeScanner: boolean;
    permissions: PermissionState[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MODELS = ["branches", "products", "tiers", "challenges", "ad-banners", "clients"] as const;
const ACTIONS: ActionType[] = ["viewing", "creating", "editing", "deleting"];

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": {
                backgroundColor: "var(--color-orange)",
                opacity: 1,
                border: 0,
            },
        },
    },
    "& .MuiSwitch-thumb": {
        width: 16,
        height: 16,
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
    },
    "& .MuiSwitch-track": {
        borderRadius: "var(--radius-full)",
        backgroundColor: "var(--color-gray-01)",
        opacity: 1,
    },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildInitialForm(initial: InitialClientData | undefined, isDuplicate?: boolean): FormState {
    if (!initial) {
        return {
            name: "",
            phone: "",
            makeOwner: false,
            makeScanner: true,
            permissions: [],
        };
    }

    return {
        name: initial.name || "",
        phone: isDuplicate ? "" : initial.phone || "",
        makeOwner: initial.isOwner || false,
        makeScanner: initial.isScanner ?? true,
        permissions: initial.permissions || [],
    };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: ClientsFormDetails;
}

export default function ClientsForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const clientId = details?.clientId;
    const isDuplicate = details?.isDuplicate;
    const isEdit = !!clientId && !isDuplicate;

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData, isDuplicate));

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "client-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.clientForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Permission Helpers ────────────────────────────────────────────────────

    const hasAction = (model: string, action: ActionType): boolean => {
        const perm = form.permissions.find((p) => p.model === model);
        return perm?.actions.includes(action) ?? false;
    };

    const ensureViewingIfOtherActions = (permissions: PermissionState[]): PermissionState[] => {
        return permissions.map((p) => {
            const hasOtherActions = p.actions.some((a) => a !== "viewing");
            if (hasOtherActions && !p.actions.includes("viewing")) {
                return { ...p, actions: ["viewing", ...p.actions] as ActionType[] };
            }
            return p;
        });
    };

    const toggleAction = (model: string, action: ActionType) => {
        const prev = form.permissions;
        const existing = prev.find((p) => p.model === model);

        let updated: PermissionState[];

        if (existing) {
            const hasIt = existing.actions.includes(action);
            if (hasIt) {
                const newActions = existing.actions.filter((a) => a !== action);
                if (newActions.length === 0) {
                    updated = prev.filter((p) => p.model !== model);
                } else {
                    updated = prev.map((p) => (p.model === model ? { ...p, actions: newActions } : p));
                }
            } else {
                updated = prev.map((p) => (p.model === model ? { ...p, actions: [...p.actions, action] } : p));
            }
        } else {
            updated = [...prev, { model, actions: [action], branches: "all" }];
        }

        setField("permissions", ensureViewingIfOtherActions(updated));
    };

    // ── Global Toggle for a Model (all actions) ───────────────────────────────

    const getModelCheckState = (model: string): "none" | "some" | "all" => {
        const perm = form.permissions.find((p) => p.model === model);
        if (!perm || perm.actions.length === 0) return "none";
        if (ACTIONS.every((a) => perm.actions.includes(a))) return "all";
        return "some";
    };

    const toggleModelAll = (model: string) => {
        const state = getModelCheckState(model);
        const prev = form.permissions;

        let updated: PermissionState[];

        if (state === "all") {
            // If all checked, uncheck all
            updated = prev.filter((p) => p.model !== model);
        } else {
            // If none or some, check all
            const existing = prev.find((p) => p.model === model);
            if (existing) {
                updated = prev.map((p) => (p.model === model ? { ...p, actions: [...ACTIONS] } : p));
            } else {
                updated = [...prev, { model, actions: [...ACTIONS], branches: "all" }];
            }
        }

        setField("permissions", updated);
    };

    // ── Global Toggle for an Action (all models) ──────────────────────────────

    const getActionCheckState = (action: ActionType): "none" | "some" | "all" => {
        const checkedCount = MODELS.filter((model) => hasAction(model, action)).length;
        if (checkedCount === 0) return "none";
        if (checkedCount === MODELS.length) return "all";
        return "some";
    };

    const toggleActionAll = (action: ActionType) => {
        const state = getActionCheckState(action);
        let updated: PermissionState[] = [...form.permissions];

        if (state === "all") {
            // If all checked, uncheck all
            updated = updated.map((p) => ({ ...p, actions: p.actions.filter((a) => a !== action) })).filter((p) => p.actions.length > 0);
        } else {
            // If none or some, check all
            for (const model of MODELS) {
                const existingIndex = updated.findIndex((p) => p.model === model);
                if (existingIndex >= 0) {
                    if (!updated[existingIndex].actions.includes(action)) {
                        updated[existingIndex] = { ...updated[existingIndex], actions: [...updated[existingIndex].actions, action] };
                    }
                } else {
                    updated.push({ model, actions: [action], branches: "all" });
                }
            }
        }

        setField("permissions", ensureViewingIfOtherActions(updated));
    };

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("clients", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!isEdit) {
            if (!form.name.trim()) {
                showError(t.clientForm.nameRequired);
                return;
            }
            if (!form.phone.trim()) {
                showError(t.clientForm.phoneRequired);
                return;
            }
        }

        const data = isEdit
            ? {
                  makeOwner: form.makeOwner,
                  makeScanner: form.makeScanner,
                  permissions: form.permissions.map((p) => ({ model: p.model, actions: p.actions, branches: p.branches })),
              }
            : {
                  name: form.name.trim(),
                  phone: form.phone.trim(),
                  makeOwner: form.makeOwner,
                  makeScanner: form.makeScanner,
                  permissions: form.permissions.map((p) => ({ model: p.model, actions: p.actions, branches: p.branches })),
              };

        submitInvoke({
            method: isEdit ? "PUT" : "POST",
            ...(isEdit ? { param: clientId } : {}),
            data,
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="clients-form" onSubmit={handleSubmit} noValidate>
            <div className="clients-form-fields">
                {/* ── Basic Info Section (only for create) ── */}
                {!isEdit && (
                    <InputGroups icon="/icons/inputs-group-icon.svg" title={t.clientForm.basicInfo}>
                        <div className="uf-row uf-row--2">
                            <TextField
                                fullWidth
                                label={t.clientForm.name}
                                value={form.name}
                                onChange={(e) => setField("name", e.target.value)}
                                sx={textFieldSx}
                            />
                            <TextField
                                fullWidth
                                label={t.clientForm.phone}
                                value={form.phone}
                                onChange={(e) => setField("phone", e.target.value)}
                                sx={textFieldSx}
                            />
                        </div>
                    </InputGroups>
                )}

                {/* ── Owner Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.clientForm.ownerSection} header={false}>
                    <div className="uf-owner-row">
                        <span className="uf-owner-label">{t.clientForm.makeOwner}</span>
                        <Switch checked={form.makeOwner} onChange={(e) => setField("makeOwner", e.target.checked)} sx={switchSx} />
                    </div>
                    <p className="uf-owner-hint">{t.clientForm.ownerHint}</p>
                </InputGroups>

                {/* ── Scanner Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.clientForm.scannerSection} header={false}>
                    <div className="uf-owner-row">
                        <span className="uf-owner-label">{t.clientForm.makeScanner}</span>
                        <Switch checked={form.makeScanner} onChange={(e) => setField("makeScanner", e.target.checked)} sx={switchSx} />
                    </div>
                    <p className="uf-owner-hint">{t.clientForm.scannerHint}</p>
                </InputGroups>

                {/* ── Permissions Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.clientForm.permissionsSection}>
                    <div className="uf-permissions-table-wrapper">
                        <table className="uf-permissions-table">
                            <thead>
                                <tr>
                                    <th>{t.permissionPopup.model}</th>
                                    {ACTIONS.map((action) => {
                                        const state = getActionCheckState(action);
                                        return (
                                            <th key={action}>
                                                <div className="uf-th-content">
                                                    <span>{t.permissionPopup[action]}</span>
                                                    <button
                                                        type="button"
                                                        className={`uf-checkbox uf-checkbox--small ${state === "all" ? "checked" : state === "some" ? "partial" : ""}`}
                                                        onClick={() => toggleActionAll(action)}
                                                        aria-label={`Toggle all ${action}`}
                                                    />
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {MODELS.map((model) => {
                                    const modelState = getModelCheckState(model);
                                    return (
                                        <tr key={model}>
                                            <td>
                                                <span className="uf-model-name">
                                                    <button
                                                        type="button"
                                                        className={`uf-checkbox ${modelState === "all" ? "checked" : modelState === "some" ? "partial" : ""}`}
                                                        onClick={() => toggleModelAll(model)}
                                                        aria-label={`Toggle all ${model}`}
                                                    />
                                                    {t.permissionPopup.models[model as keyof typeof t.permissionPopup.models]}
                                                </span>
                                            </td>
                                            {ACTIONS.map((action) => (
                                                <td key={action}>
                                                    <button
                                                        type="button"
                                                        className={`uf-checkbox ${hasAction(model, action) ? "checked" : ""}`}
                                                        onClick={() => toggleAction(model, action)}
                                                        aria-label={`${action} ${model}`}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="uf-footer">
                <button type="button" className="uf-btn uf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.clientForm.cancel}
                </button>
                <button type="submit" className="uf-btn uf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="uf-spinner" /> : isEdit ? t.clientForm.update : t.clientForm.create}
                </button>
            </div>
        </form>
    );
}
